// University of Pittsburgh (Pittsburgh campus): public college page, official
// facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Pitt's Common Data Set 2025-2026 for the Pittsburgh campus, linked from
//    the Institutional Research and Analytics Common Data Set page: C1
//    applicants, admits and enrollees for Fall 2025 (C1 gives men and women
//    only; the totals here are their sums, and the unknown-sex lines and the
//    residency table are left blank), C8 test policy for Fall 2027, C21/C22
//    (no early decision, no early action), G1/G5 2026-2027 costs.
//  - Pitt Admissions: First-Year Student (the admissions process, the
//    December 1 scholarship date, score use), Test-Optional (the
//    extension through fall 2028) and Guaranteed Admission Programs. The
//    pages say "The Fall 2027 Application is open!". Pitt admits on a rolling
//    basis and publishes no decision dates. The testing line joins the
//    Test-Optional page with the First-Year page's score-use sentence
//    (highest ACT composite, highest SAT superscore).
//  - College Scorecard API, school id 215293 (net price by family income).
// Cost: the CDS G1/G5 items for a resident student. G4 says tuition varies by
// program and 36.8% of full-time undergraduates pay more than the G1 figures;
// the page note says so.
// Departures: none from the CDS. Nothing here is profile-derived, an odds
// figure or a student record. Re-check a figure against its source before
// editing it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'University of Pittsburgh Common Data Set 2025-2026 (Pittsburgh campus)',
        url: 'https://www.ir.pitt.edu/university-information/common-data-set',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Pitt Admissions, First-Year Student',
        url: 'https://admissions.pitt.edu/first-year-student/',
        read: '2026-10-01',
    },
    testOptional: {
        label: 'Pitt Admissions, Test-Optional',
        url: 'https://admissions.pitt.edu/test-optional/',
        read: '2026-10-01',
    },
    gap: {
        label: 'Pitt Admissions, Guaranteed Admission Programs',
        url: 'https://admissions.pitt.edu/guaranteed-admissions-programs/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?215293-University-of-Pittsburgh-Pittsburgh-Campus',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-pittsburgh',
    kbId: 'university_of_pittsburgh',
    name: 'University of Pittsburgh',
    shortName: 'Pitt',
    aliases: ['Pitt'],
    location: 'Pittsburgh, PA',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Pitt brand color palettes); decoration only.
    branding: {
        primary: '#003594',
        secondary: '#FFB81C',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_pittsburgh_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 64724,
        admitted: 38477,
        enrolled: 5876,
        source: 'cds',
        where: 'section C1 (men and women added together)',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Pitt\'s Common Data Set reports one first-year acceptance rate for the Pittsburgh campus, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'Pitt has no early decision or early action plan. It admits on a rolling basis, and applying by December 1 brings automatic scholarship consideration.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to one of Pitt\'s first-year entry schools. You can also ask on the application to be considered for a Guaranteed Admission Program into a Pitt graduate or professional school. Three of them have their own dates and need test scores:',
        rows: [
            { school: 'Medicine Guarantee', requirement: 'Apply by October 15 to the Dietrich School of Arts and Sciences, the School of Public Health, Computational Biology in the School of Computing and Information, or Bioengineering in the Swanson School of Engineering. A supplemental application, test scores and the STARS record are due November 1. A minimum SAT of 1500 or ACT of 34. Not open to international students.' },
            { school: 'Physician Assistant Guarantee', requirement: 'Application and all materials by December 15. Top 10 percent of your class (if applicable) and a minimum SAT of 1500 or ACT of 34.' },
            { school: 'Dental Medicine Guarantee', requirement: 'Apply to the Dietrich School of Arts and Sciences, with all materials by January 15. Top 10 percent of your class and a minimum SAT of 1470 or ACT of 33. Not open to international students.' },
        ],
        note: 'Pitt notifies only the applicants it selects for a guarantee. Most other guarantees are open to test-optional applicants.',
        source: 'gap',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Admission', value: 'Rolling' },
        rows: [
            { plan: 'Rolling admission', deadline: 'No set deadline', decision: null, binding: false },
            { plan: 'Scholarship consideration', deadline: 'December 1', decision: null, binding: false },
        ],
        note: 'Pitt reviews applications and makes decisions throughout the year, and does not publish decision dates. A complete application with all materials by December 1 is automatically considered for Pitt academic scholarships. You can use the Pitt Application or the Common App; Pitt has no preference. The STARS record, where you enter your high school courses and grades to match your transcript, is required of every first-year applicant except international students.',
        source: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Pitt does not require the ACT or SAT for first-year or transfer applicants, including international and homeschool students. If you send scores, Pitt uses your highest ACT composite and your highest SAT superscore, and accepts an ACT superscore sent directly from ACT. Once your application is under review, you cannot change your test-optional status. If you apply without scores, a personal statement or the Common App essay is required.',
        source: 'testOptional',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for admission, but considered if submitted, for fall 2027 applicants.',
        cdsWhere: 'section C8',
    },

    currentChanges: [
        { topic: 'Scores for three guarantees', line: 'Starting August 1, 2026, the Medicine, Dental Medicine and Physician Assistant Guaranteed Admission Programs require SAT or ACT scores; a test-optional application is not reviewed for them.', source: 'gap' },
    ],

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [
            { topic: 'Testing', line: 'Pitt has extended its test-optional policy through fall 2028: ACT or SAT scores will not be required of first-year or transfer students applying to enter in fall 2028.', source: 'testOptional' },
        ],
        source: 'testOptional',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [21594, 43328] },
            { item: 'Required fees', amounts: [2152, 2152] },
            { item: 'Housing (on campus)', amounts: [9210, 9210] },
            { item: 'Food (on-campus meal plan)', amounts: [6220, 6220] },
            { item: 'Books and supplies', amounts: [596, 596] },
            { item: 'Transportation', amounts: [1088, 1088] },
            { item: 'Other expenses', amounts: [2478, 2478] },
        ],
        totals: [43338, 65072],
        note: 'For a full-time undergraduate living on campus, fall and spring. Tuition varies by program: Pitt reports that 36.8% of full-time undergraduates pay more than the tuition and fees shown here.',
        source: 'cds',
        where: 'sections G1, G4 and G5',
        netPrice: {
            average: 29362,
            byIncome: [
                { income: '$0 to $30,000', amount: 16477 },
                { income: '$30,001 to $48,000', amount: 15485 },
                { income: '$48,001 to $75,000', amount: 19080 },
                { income: '$75,001 to $110,000', amount: 28146 },
                { income: 'Over $110,000', amount: 35864 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Pitt\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
